import { createContext, useContext, useMemo } from 'react';
import type { CurrentMemberFragment } from 'generated/graphql';
import { useMaybeAppData } from '@modules/root';
import { useRouteLoaderData } from 'react-router';
import type { RootData } from '~/root';
import type { GuestUser, User } from '~/server/types';

/**
 * Inside it every user hook reports a signed-out visitor, keeping the feature flags of whoever is
 * really there. The Business Manager's form previews use it so the owner sees the guest's form,
 * not one filled in with their own details.
 */
export const AsGuestContext = createContext(false);

function asGuest(user: User): GuestUser {
    return {
        __typename: 'Guest',
        ID: 0,
        APID: user.APID,
        EcommerceBookingURL: user.EcommerceBookingURL,
        FeatureFlags: user.FeatureFlags,
    };
}

/**
 * Check if a user is logged-in.
 * @param user The user to check.
 * @returns The user is logged-in.
 */
export function isUserLoggedIn(
    user: User | null | undefined,
): user is CurrentMemberFragment {
    return user?.__typename === 'Me';
}

/**
 * Hook to get the current user.
 * @returns The current user.
 */
export function useUser(): User {
    const appData = useMaybeAppData();
    const rootData = useRouteLoaderData('root') as RootData | undefined;
    const guestOnly = useContext(AsGuestContext);

    const user = appData?.user ?? rootData?.user;
    const guest = useMemo(
        () => (guestOnly && user ? asGuest(user) : null),
        [guestOnly, user],
    );
    if (!user) {
        throw new Error(
            'useUser: missing user on routes/_app and root loaders',
        );
    }
    return guest ?? user;
}

/**
 * Hook to get the current user and if they are logged-in.
 * @returns The user and the user logged-in state.
 */
export function useUserLoggedIn() {
    const user = useUser();
    return useMemo(() => {
        if (isUserLoggedIn(user)) {
            return { user, isLoggedIn: true } as const;
        }
        return { user, isLoggedIn: false } as const;
    }, [user]);
}
